import React, {useEffect, useMemo, useState} from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import LMTable from "../Tables/LMTable";
import {LMColumn} from "../Tables/types";
import {DbCustomer} from "sharedlibs";
import {useTranslation} from "react-i18next";
import CustomerService from "../../services/CustomerService";
import {compareCustomerNumbers, CUSTOMER_NUMBER_SORT_MODEL} from "../../utils/customerNumberSort";

export interface AssignCustomersDialogProps {
  open: boolean;
  onClose: () => void;
  onAssigned: (assignedIds: string[]) => void;
  salespersonId: string;
}

export default function AssignCustomersDialog({
  open,
  onClose,
  onAssigned,
  salespersonId,
}: Readonly<AssignCustomersDialogProps>) {
  const {t} = useTranslation();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<DbCustomer[]>([]);
  const [selection, setSelection] = useState<string[]>([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSelection([]);
    setSearch("");
    setLoading(true);
    CustomerService.getUnassignedCustomers()
      .then((list) => setCustomers(list))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => {
      const hay = [
        c.name,
        c.customerNumber,
        c.doctorName,
        c.address?.city ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [customers, search]);

  const columns: LMColumn<DbCustomer>[] = [
    {field: "name", headerName: "objects.customer.customerName", flex: 2},
    {
      field: "customerNumber",
      headerName: "objects.customer.customerNumber",
      flex: 1,
      sortComparator: compareCustomerNumbers,
    },
    {
      field: "city",
      headerName: "objects.address.location",
      flex: 1,
      valueGetter: (_value, row) => row.address?.city ?? "",
    },
    {
      field: "doctorName",
      headerName: "objects.customer.doctorName",
      flex: 1.5,
    },
  ];

  async function handleAssign() {
    if (selection.length === 0) return;
    setSubmitting(true);
    setError(null);
    // Selection is single-select by design (see LMTable's SelectionMode), so
    // there is exactly one id here. onAssigned still takes a list so the caller
    // doesn't have to change if bulk assignment is ever reintroduced.
    const id = String(selection[0]);
    try {
      await CustomerService.assignSalesperson(id, salespersonId);
      onAssigned([id]);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{t("salesperson.assignCustomers")}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
          {t("salesperson.assignCustomersHint")}
        </Typography>

        <TextField
          fullWidth
          size="small"
          placeholder={t("common.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{mb: 2}}
        />

        {error && (
          <Alert severity="error" sx={{mb: 2}}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{display: "flex", justifyContent: "center", py: 4}}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{py: 3, textAlign: "center", color: "text.secondary"}}>
            <Typography variant="body2">
              {customers.length === 0
                ? t("salesperson.noUnassignedCustomers")
                : t("salesperson.noUnassignedCustomersForSearch")}
            </Typography>
          </Box>
        ) : (
          <Box sx={{width: "100%"}}>
            <LMTable<DbCustomer>
              rows={filtered}
              getRowId={(row) => row.id ?? ""}
              columns={columns}
              selectionMode="single"
              selectionModel={selection}
              selectionHandler={setSelection}
              pageSizeOptions={[10, 25, 50]}
              options={{sortModel: CUSTOMER_NUMBER_SORT_MODEL}}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t("activities.cancel")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAssign}
          disabled={submitting || selection.length === 0}
        >
          {submitting
            ? t("salesperson.assigning")
            : t("salesperson.assignSelected", {count: selection.length})}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
